import { utils } from 'shared/intrasyncUtils';
import { databaseDate, getLastWorkday } from 'shared/intrasyncUtils';
import { getObserverTree, makeAutoObservable, reaction, toJS } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

import { UIStore } from "@intrasync/stores/features";

interface Hold {
  id: number;
  holdText: string;
  userEmail: string;
  relationValue: string;
  relationKey: string;
  holdType: string;
  jobNumber: number;
  completed: string;
}

interface TableStats {
  tableNumber: number;
  tablePanelCount: number;
  gridRow: number;
  gridPosition: number;
  gridSize: number;
}

interface ProductionScheduleItem {
  id: string;
  scheduleDate: string;
  formId?: string;
  productId: string;
  salesOrderLineId?: string;
  quantity: number;
  status: string;
  itemType: "production-schedule";
  orderNumber?: string;
  jobName?: string;
  structureNumber?: string;
}

// const mixCheck = mix => (mix && mix !== '0' && mix !== 0 && mix) || '';
/**
 * @class castingMapStore
 * @description The store where all functions are stored related to the CastingMap
 * are stored.
 */
class CastingMapStore {
  debugMode = false;
  enableReportComputedObservability = false;

  castingMapLoading = false;
  mockupProductType = "";
  currentLayoutToBeEdited = {};
  newLayout = {};
  isAddingNewLayout = false;
  panelEdits = {};

  syncStatus = false;
  isPrinting = false;

  // = where the job table is rendered,  0 = empty space
  jobToAssign = "";

  mobileTableNumber = 1;

  listOfComputedValues = [];

  recastDialogDate = "1970-01-01";

  editTableNumber = false;

  // Toggle to show all production schedules regardless of date
  showAllProductionSchedules = false;

  rootStore: RootStore;

  castingMapPanelEditsDispose;

  /**
   * @constructor
   * @description constructor of the castingMapStore where services are initialized
   */
  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
    });
    this.rootStore = rootStore;

    this.castingMapPanelEditsDispose = reaction(
      () =>
        this.panelEdits &&
        Object.keys(this.panelEdits).length > 0 &&
        this.panelToDisplay,
      (value) => {
        if (!value) {
          this.resetPanelEdits();
        }
      }
    );
  }

  dispose = () => {
    this.castingMapPanelEditsDispose && this.castingMapPanelEditsDispose();
  };

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  /**
   * @action setRecastDialogDate
   * @description sets the recast dialog date
   * @param {string} value
   * @returns {void}
   * @memberof castingMapStore
   */
  setRecastDialogDate = (value: string): void => {
    this.recastDialogDate = value;
  };

  setIsPrinting = (state: boolean) => {
    this.isPrinting = state;
  };

  setMobileTableNumber = (newValue: number) => {
    this.mobileTableNumber = newValue;
  };

  setCurrentLayoutToBeEdited = (layout: Record<string, unknown>): void => {
    this.currentLayoutToBeEdited = layout;
  };

  toggleIsAddingNewLayout = () => {
    this.isAddingNewLayout = !this.isAddingNewLayout;
  };
  /**
   * @function submitTableSettings
   * @description changes table display based on selected settings
   */
  submitTableSettings = () => {
    if (this.editTableNumber) this.toggleEditTableNumber();
  };

  setPanelEdits = (key: string, value: unknown) => {
    // if (key.includes("Time")) {
    //   const timeWithSeconds = `${value}:00`;
    //   this.panelEdits = { ...this.panelEdits, [key]: timeWithSeconds };
    //   return;
    // }
    this.panelEdits = { ...this.panelEdits, [key]: value };
  };

  resetPanelEdits = () => {
    this.panelEdits = {};
  };

  /**
   * @function submitCastingMapDialog
   * @description closes the panel Production Floor Map modal and updates the panel
   */
  submitCastingMapDialog = async () => {
    console.log("submitCastingMapDialog");
    console.log("this.panelToDisplay", this.panelToDisplay);
    console.log("this.panelEdits", this.panelEdits);
    if (
      !Object.keys(this.panelEdits).length ||
      !this.panelToDisplay?.jobNumber ||
      !this.panelToDisplay?.controlNumber
    ) {
      console.warn("Nothing was edited, should be disabled input");
      return;
    }
    console.log("this.panelToDisplay", this.panelToDisplay);
    try {
      const panelStore = (await this.rootStore.panelStore) as ServiceStore;
      await panelStore.patch(null, toJS(this.panelEdits), {
        query: {
          jobNumber: this.panelToDisplay.jobNumber,
          controlNumber: this.panelToDisplay.controlNumber,
        },
      });
    } catch (err) {
      console.warn(err);
    }

    this.resetPanelEdits();
  };

  toggleEditTableNumber = () => {
    this.editTableNumber = !this.editTableNumber;
  };

  createNewPanelHold = async (data: { reasonForHold: string }) => {
    if (!this.dialogPanel?.controlUUID) return;
    const { reasonForHold } = data;
    const authStore = this.rootStore.authStore;
    const holdStore = this.rootStore.holdStore as ServiceStore;
    if (!authStore?.currentUser?.email) return;
    const { email } = authStore.currentUser;
    const newPanelHold = {
      holdText: reasonForHold,
      userEmail: email.replace("@intrasyncindustrial.com", ""),
      relationValue: this.dialogPanel.controlUUID,
      relationKey: "controlUUID",
      holdType: "panel",
      jobNumber: this.dialogPanel.jobNumber,
    };

    await holdStore.create(newPanelHold);
  };

  checkForStaleCastingDates = async () => {
    this.debugMode && console.warn("checkForStaleCastingDates");
    const panelStore = this.rootStore.panelStore as ServiceStore;
    const routerStore = this.rootStore.routerStore;
    const notificationStore = this.rootStore.notificationStore;
    const castingDate = toJS(routerStore?.queryParams?.castingDate);
    this.debugMode && console.warn("castingDate", castingDate);
    const foundPanelsOnClient = panelStore.entities
      .slice()
      .filter((panel) => panel.castingDate === castingDate);
    this.debugMode && console.warn("foundPanelsOnClient", foundPanelsOnClient);
    if (foundPanelsOnClient.length) {
      this.debugMode &&
        console.warn("foundPanelsOnClient", foundPanelsOnClient);
      const panelPlantIds = foundPanelsOnClient.map((panel) => panel.id);
      panelStore
        .find({
          query: {
            id: { $in: [...panelPlantIds] },
          },
        })
        .then((foundFromServer) => {
          this.debugMode && console.warn("foundFromServer", foundFromServer);
          if (
            foundFromServer?.data?.length &&
            foundFromServer.data.length !== panelPlantIds.length
          ) {
            notificationStore?.addNotification({
              message: "Clearing stale data",
              options: { variant: "Warning", autoHideduration: 3000 },
            });
            this.debugMode && console.warn("NEEDS PURGE");
            const panelIdsFromServer = foundFromServer.data.map(
              (panel) => panel.id
            );
            const listOfPanelsToClear = panelPlantIds.filter(
              (plantPanelId) =>
                !panelIdsFromServer.includes(String(plantPanelId))
            );

            this.debugMode &&
              console.warn("listOfPanelsToClear", listOfPanelsToClear);
            listOfPanelsToClear.map((panelToRemove) => {
              panelStore.removeEvent({ id: panelToRemove as string });
              return panelToRemove;
            });
          }
        });
    }
  };

  removePanelHold = async (panelHold: Hold) => {
    this.debugMode && console.warn("removing hold", panelHold);
    const holdStore = this.rootStore.holdStore as ServiceStore;
    if (!panelHold.id) return;

    return await holdStore.remove(panelHold.id);
  };

  findPanelOnHold = async (value: string) => {
    const holdStore = this.rootStore.holdStore as ServiceStore;
    return await holdStore.find({
      query: {
        relationValue: value,
        holdType: "panel",
      },
    });
  };

  completePanelHold = async (panelHold: Hold): Promise<void> => {
    this.debugMode && console.warn("completing hold", panelHold);
    const holdStore = this.rootStore.holdStore as ServiceStore;
    if (!panelHold.id) return;

    await holdStore.patch(panelHold.id, {
      completed: databaseDate(),
    });
  };

  get completedPanelHolds() {
    const holdStore = this.rootStore.holdStore as ServiceStore;
    return holdStore.entities.filter(
      (panelHold) =>
        panelHold.relationValue === this.dialogPanel?.controlUUID &&
        panelHold.holdType === "panel" &&
        panelHold.completed !== "1970-01-01"
    );
  }

  get currentPanelHold() {
    const holdStore = this.rootStore.holdStore as ServiceStore;
    if (!this.dialogPanel?.controlUUID) {
      return null;
    }

    const panelHoldExists = holdStore.entities.find(
      (panelHold) =>
        panelHold.relationValue === this.dialogPanel?.controlUUID &&
        panelHold.holdType === "panel" &&
        panelHold.completed === "1970-01-01"
    );

    if (panelHoldExists) return panelHoldExists;

    return null;
  }

  get dialogPanel() {
    const tree = getObserverTree(this, "dialogPanel");
    console.log("tree", tree);
    const routerStore = this.rootStore.routerStore;
    if (!routerStore?.queryParams?.dialogContext) return null;
    const { queryParams } = routerStore;
    const foundPlantPanel = this.currentPlantPanels
      .slice()
      .find((panel) => panel.controlUUID === queryParams?.dialogContext);
    if (foundPlantPanel) {
      const foundCurrentRevitPanel = this.currentRevitPanels.find(
        (revitPanel) => revitPanel?.controlUUID === foundPlantPanel.controlUUID
      );
      if (foundCurrentRevitPanel) {
        return { ...foundCurrentRevitPanel, ...foundPlantPanel };
      }
      return { ...foundPlantPanel };
    }
    return null;
  }

  get tableList(): TableStats[] {
    const castingMapLayoutStore = this.rootStore
      .castingMapLayoutStore as ServiceStore;
    return castingMapLayoutStore.entities
      .slice()
      .filter(
        (table) =>
          (table as unknown as { startDate: string; endDate: string })
            .startDate <= this.castingDisplayDate &&
          (((table as unknown as { startDate: string; endDate: string })
            .endDate !== "1970-01-01" &&
            (table as unknown as { startDate: string; endDate: string })
              .endDate >= this.castingDisplayDate) ||
            (table as unknown as { startDate: string; endDate: string })
              .endDate === "1970-01-01")
      ) as unknown as TableStats[];
  }

  get availableTablesFromList() {
    return this.tableList
      .map((i) => String(i.tableNumber))
      .sort((a, b) => Number(a) - Number(b));
  }

  get currentPlantPanels() {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    return panelStore.entities
      .slice()
      .filter((panel) => panel.castingDate === this.castingDisplayDate);
  }

  get currentRevitPanels() {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    return this.currentPlantPanels.map((plantPanel) => {
      return panelStore.entities.find(
        (p) => p.controlUUID === plantPanel.controlUUID
      );
    });
  }

  get castingMapData() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    const jobs: {
      name: string;
      jobNumber: number;
      sqft: number;
      recastSqFt: number;
      castingMapColor: string;
    }[] = [];
    // console.log('currentPlantPanels', this.currentPlantPanels);
    const panels = [...this.currentPlantPanels].map((item) => {
      let panel = { ...item };
      let job = jobStore.entities.find((item) => {
        // console.log("CastingMapStore jobStore.entities", item);
        return utils.isSameJobNumber(
          item.jobNumber as number,
          panel.jobNumber as number
        );
      });
      if (job && job.jobNumber) {
        // Job now contains all consolidated fields from job-plant
        // No need to find separate jobPlant entity

        // Add color and jobName to panel
        panel = {
          ...panel,
          color: job.castingMapColor ? job.castingMapColor : "Unassigned",
          jobName: job.name ? job.name : "Error Loading Job",
        };

        // Get matching revitPanel for plantPanel
        const foundCurrentRevitPanel = this.currentRevitPanels
          .slice()
          .filter((revitPanel) => revitPanel?.controlUUID)
          .find((revitPanel) => revitPanel?.controlUUID === panel.controlUUID);

        const panelSqFt =
          panel.castingDate === this.castingDisplayDate
            ? Math.round(Number(foundCurrentRevitPanel?.totalSqFt) || 0)
            : 0;

        const recastSqFt =
          panel.castingDate === this.castingDisplayDate &&
          panel.castingType === "Recast"
            ? Math.round(Number(foundCurrentRevitPanel?.totalSqFt) || 0)
            : 0;

        const foundIndex = jobs.findIndex((item) =>
          utils.isSameJobNumber(
            job?.jobNumber as number,
            item.jobNumber as number
          )
        );

        if (foundIndex > -1) {
          jobs[foundIndex].sqft = jobs[foundIndex].sqft + panelSqFt;
          jobs[foundIndex].recastSqFt =
            jobs[foundIndex].recastSqFt + recastSqFt;
        } else {
          jobs.push({
            name: job.name as string,
            jobNumber: job.jobNumber as number,
            sqft: panelSqFt,
            recastSqFt: recastSqFt,
            castingMapColor: job.castingMapColor as string,
          });
        }
        return { ...foundCurrentRevitPanel, ...panel };
      }
      return panel;
    });

    const panelStore = this.rootStore.panelStore as ServiceStore;

    const foundMarkChanges = panels.filter((panel) => {
      const foundPanelsYesterday = panelStore.entities.filter((p) => {
        const yesterday = getLastWorkday(panel.castingDate as string);
        return (
          p.tableNumber === panel.tableNumber &&
          p.castingDate === yesterday &&
          p.markNumber !== panel.markNumber
        );
      });

      if (foundPanelsYesterday && foundPanelsYesterday.length) return true;

      return false;
    });

    return {
      panels: [...panels].sort(
        (a: { [x: string]: unknown }, b: { [x: string]: unknown }) => {
          const jobA = a as { jobNumber: number; controlNumber: number };
          const jobB = b as { jobNumber: number; controlNumber: number };
          if (utils.isSameJobNumber(jobA.jobNumber, jobB.jobNumber))
            return jobA.controlNumber > jobB.controlNumber ? 1 : -1;

          return jobA.jobNumber > jobB.jobNumber ? 1 : -1;
        }
      ),
      jobs,
      foundMarkChanges,
    };
  }

  /**
   * @function panelsWithoutTable - computed
   * @description returns list of panels scheduled for selected day without table number assigned
   * @returns {array}
   */
  get panelsWithoutTable() {
    return this.castingMapData.panels
      .slice()
      .filter((panel) => !panel.tableNumber)
      .map((i) => {
        return { ...i };
      });
  }

  //Computeds
  get panelToDisplay() {
    const routerStore = this.rootStore.routerStore;

    if (!routerStore?.queryParams?.dialogContext) return null;
    const { queryParams } = routerStore;
    const panelStore = this.rootStore.panelStore as ServiceStore;
    if (!panelStore?.entities?.length) return null;

    const foundPlantPanel = this.currentPlantPanels
      .slice()
      .find(
        (item) =>
          `${item.jobNumber}${item.controlNumber}` ===
          queryParams?.dialogContext
      );

    const foundRevitPanel = this.currentRevitPanels
      .slice()
      .find(
        (revitPanel) =>
          revitPanel &&
          `${revitPanel.jobNumber}${revitPanel.controlNumber}` ===
            `${foundPlantPanel?.jobNumber ?? ""}${
              foundPlantPanel?.controlNumber ?? ""
            }`
      );

    console.log("panelToDisplay foundRevitPanel", foundRevitPanel);
    console.log("panelToDisplay foundPlantPanel", foundPlantPanel);

    return { ...foundRevitPanel, ...foundPlantPanel };
  }

  get panelToDisplayRenderData() {
    if (!this.panelToDisplay) return {};
    const currentItem = { ...this.panelToDisplay };

    if (Object.keys(this.panelEdits).length) {
      // Only if we have edited a value
      for (const [key, value] of Object.entries(this.panelEdits)) {
        // Loop over each key value pair and apply to our currentItem
        currentItem[key] = value;
      }
    }

    return currentItem;
  }

  get panelDetailsValidateSubmit() {
    return !Object.keys(this.panelEdits).length;
  }

  get jobStatsDialog() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    const routerStore = this.rootStore.routerStore;
    return jobStore.entities
      .slice()
      .find((j) =>
        utils.isSameJobNumber(
          j.jobNumber as number,
          routerStore?.queryParams?.dialogContext as number
        )
      );
  }

  get jobPanelStats() {
    const routerStore = this.rootStore.routerStore;
    const panelStore = this.rootStore.panelStore as ServiceStore;
    const jobNumber = routerStore?.queryParams?.dialogContext as number;

    if (!jobNumber) return null;

    // Filter panels by job number
    const jobPanels = panelStore.entities
      .slice()
      .filter((panel) =>
        utils.isSameJobNumber(panel.jobNumber as number, jobNumber)
      );

    // Calculate statistics
    const totalPanels = jobPanels.length;
    const totalSqFt = jobPanels.reduce(
      (sum, panel) => sum + (Number(panel.totalSqFt) || 0),
      0
    );

    // Group by casting date
    const panelsByCastingDate = jobPanels.filter(
      (panel) => panel.castingDate === this.castingDisplayDate
    );

    // Group by product type
    const panelsByType = jobPanels.reduce((acc, panel) => {
      const type = (panel.productType || "Unknown") as string;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Calculate panels with/without tables
    const panelsWithTable = jobPanels.filter(
      (panel) => panel.tableNumber
    ).length;
    const panelsWithoutTable = jobPanels.filter(
      (panel) => !panel.tableNumber
    ).length;

    // Get panels by status
    const castedPanels = jobPanels.filter(
      (panel) => panel.castingDate && panel.castingDate !== "1970-01-01"
    ).length;

    const recastPanels = jobPanels.filter(
      (panel) => panel.castingType === "Recast"
    ).length;

    return {
      jobNumber,
      totalPanels,
      totalSqFt: Math.round(totalSqFt),
      panelsByCastingDate: panelsByCastingDate.length,
      panelsByType,
      panelsWithTable,
      panelsWithoutTable,
      castedPanels,
      recastPanels,
      avgSqFtPerPanel:
        totalPanels > 0 ? Math.round(totalSqFt / totalPanels) : 0,
    };
  }

  get translation() {
    const uiStore = this.rootStore.uiStore as UIStore;
    if (uiStore?.currentUserLanguage === "spanish") {
      return {
        jobStatsTitle: "Estadisticas Laborales",
        // castingMapDetailsTitle: 'Detalles del Mapa de Casting',
        gfrcSqFt: "Pies Cuadrados (GFRC)",
        precastSqFt: "Pies Cuadrados (Precast)",
        concreteSqFt: "Pies Cuadrados (Concreto)",
        thinshellSqFt: "Pies Cuadrados (Thinshell)",
        noTableSqFt: "Pies Cuadrados Sin Plataforma",
        totalSquareFeet: "Pies Cuadrados (Total)",
        castingMapDetails: "Detalles del Mapa de Casting",
        create: "Crear",
        print: "Imprimir",
        forwardOneDay: "Adelanta 1 Día",
        backOneDay: "Retroceder 1 Día",
        frameReadyLabel: "Marco Listo",
        moldReadyLabel: "Molde Listo",
        moldQaLabel: "Molde QA",
        rebarLabel: "Varilla",
        rebarQaLabel: "Varilla QA",
      };
    }
    return {
      jobStatsTitle: "Job Stats",
      // castingMapDetailsTitle: 'Production Floor Map Details',
      gfrcSqFt: "GFRC Square Feet",
      precastSqFt: "Precast Square Feet",
      concreteSqFt: "Concrete Square Feet",
      thinshellSqFt: "Thinshell Square Feet",
      noTableSqFt: "SqFt Without Tables",
      totalSquareFeet: "Total Square Feet",
      castingMapDetails: "Production Floor Map Details",
      create: "Create",
      print: "Print",
      forwardOneDay: "Forward 1 day",
      backOneDay: "Back 1 day",
      frameReadyLabel: "Frame Ready",
      moldReadyLabel: "Mold Ready",
      moldQaLabel: "Mold QA",
      rebarLabel: "Rebar",
      rebarQaLabel: "Rebar QA",
    };
  }

  get castingDisplayDate() {
    return this.rootStore.routerStore?.queryParams?.castingDate as string;
  }

  /**
   * @computed currentProductionSchedules
   * @description Returns production schedules for the current casting display date
   * These are items from manhole/sales order conversions that need to be displayed on the floor map
   */
  get currentProductionSchedules(): ProductionScheduleItem[] {
    const productionSchedulesStore = this.rootStore[
      "production-schedulesStore"
    ] as ServiceStore | undefined;
    const schedulingSalesOrdersStore = this.rootStore[
      "scheduling-sales-ordersStore"
    ] as ServiceStore | undefined;

    if (!productionSchedulesStore?.entities?.length) {
      return [];
    }

    // Filter production schedules by the current display date (or show all if toggled)
    const schedulesForDate = productionSchedulesStore.entities
      .slice()
      .filter(
        (schedule) =>
          (this.showAllProductionSchedules || schedule.scheduleDate === this.castingDisplayDate) &&
          schedule.status !== "cancelled"
      );

    // Enrich with sales order information if available
    return schedulesForDate.map((schedule) => {
      let orderInfo: {
        orderNumber?: string;
        jobName?: string;
        structureNumber?: string;
      } = {};

      // Try to find the associated sales order for additional context
      if (schedule.salesOrderLineId && schedulingSalesOrdersStore?.entities) {
        const salesOrderLinesStore = this.rootStore[
          "sales-order-linesStore"
        ] as ServiceStore | undefined;
        if (salesOrderLinesStore?.entities) {
          const orderLine = salesOrderLinesStore.entities.find(
            (line) => line.id === schedule.salesOrderLineId
          );
          if (orderLine?.salesOrderId) {
            const salesOrder = schedulingSalesOrdersStore.entities.find(
              (order) => order.id === orderLine.salesOrderId
            );
            if (salesOrder) {
              orderInfo = {
                orderNumber: salesOrder.orderNumber as string,
                jobName: salesOrder.jobName as string,
                structureNumber: salesOrder.structureNumber as string,
              };
            }
          }
        }
      }

      return {
        id: schedule.id as string,
        scheduleDate: schedule.scheduleDate as string,
        formId: schedule.formId as string | undefined,
        productId: schedule.productId as string,
        salesOrderLineId: schedule.salesOrderLineId as string | undefined,
        quantity: schedule.quantity as number,
        status: schedule.status as string,
        itemType: "production-schedule" as const,
        ...orderInfo,
      };
    });
  }

  /**
   * @computed productionScheduleStats
   * @description Returns statistics about production schedules for the current date
   */
  get productionScheduleStats() {
    const schedules = this.currentProductionSchedules;

    return {
      totalItems: schedules.length,
      totalQuantity: schedules.reduce((sum, s) => sum + (s.quantity || 0), 0),
      byStatus: schedules.reduce((acc, s) => {
        const status = s.status || "unknown";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
    };
  }

  /**
   * @action loadProductionSchedulesForDate
   * @description Loads production schedules for a specific date from the server
   */
  loadProductionSchedulesForDate = async (date?: string) => {
    const targetDate = date || this.castingDisplayDate;
    if (!targetDate) return;

    const productionSchedulesStore = this.rootStore[
      "production-schedulesStore"
    ] as ServiceStore | undefined;

    if (!productionSchedulesStore) {
      this.debugMode &&
        console.warn("production-schedulesStore not available");
      return;
    }

    try {
      await productionSchedulesStore.find({
        query: {
          scheduleDate: targetDate,
          status: { $ne: "cancelled" },
        },
      });
    } catch (error) {
      console.error("Failed to load production schedules:", error);
    }
  };

  /**
   * @action toggleShowAllProductionSchedules
   * @description Toggles showing all production schedules regardless of date
   */
  toggleShowAllProductionSchedules = () => {
    this.showAllProductionSchedules = !this.showAllProductionSchedules;

    // If toggling on, load all schedules
    if (this.showAllProductionSchedules) {
      this.loadAllProductionSchedules();
    }
  };

  /**
   * @action loadAllProductionSchedules
   * @description Loads all production schedules without date filtering
   */
  loadAllProductionSchedules = async () => {
    const productionSchedulesStore = this.rootStore[
      "production-schedulesStore"
    ] as ServiceStore | undefined;

    if (!productionSchedulesStore) {
      this.debugMode &&
        console.warn("production-schedulesStore not available");
      return;
    }

    try {
      await productionSchedulesStore.find({
        query: {
          status: { $ne: "cancelled" },
          $limit: 100, // Reasonable limit for all schedules
        },
      });
    } catch (error) {
      console.error("Failed to load all production schedules:", error);
    }
  };
}

export default CastingMapStore;
